/**
 * Проверка: соберутся ли вообще составы драфта при заданном бюджете.
 *
 * Зачем: бюджет команды задаётся руками, а цены игроков считаются по рангу. Если
 * в турнир пришли сильные игроки, вполне реален расклад, когда последний капитан
 * физически не может добрать пятого — драфт встаёт посреди процесса, и чинить
 * это уже поздно. Дешевле сказать организатору заранее.
 *
 * Модель повторяет то, что делает бэкенд при создании команд
 * (DraftService: `remainingBudget = captainBudget - captainCost`):
 * бюджет команды включает цену САМОГО капитана, остальное идёт на пики.
 */

export interface DraftFeasibilityInput {
  /** Бюджет команды, включая цену капитана. */
  budget: number;
  /** Размер команды вместе с капитаном (обычно 5). */
  teamSize: number;
  /** Сколько команд в драфте. */
  teamsCount: number;
  /** Цены назначенных капитанов. Может быть меньше, чем команд. */
  captainCosts: number[];
  /** Цены остальных доступных игроков (без капитанов). */
  poolCosts: number[];
}

export type DraftFeasibilityReason =
  | "ok"
  | "not_enough_players"
  | "budget_too_low"
  | "captain_over_budget";

export interface DraftFeasibilityResult {
  feasible: boolean;
  reason: DraftFeasibilityReason;
  /** Минимальный бюджет, при котором составы собираются (по найденному раскладу). */
  minBudgetNeeded: number;
  /** Сколько не хватает при текущем бюджете (0, если хватает). */
  shortfall: number;
  /** Доказанно ли, что меньше нельзя (найденный расклад совпал с нижней границей). */
  exact: boolean;
  playersNeeded: number;
  playersAvailable: number;
  /** Стоимость составов в найденном раскладе, по убыванию. */
  teamTotals: number[];
  /** Сколько капитанов пришлось домыслить (ещё не назначены). */
  assumedCaptains: number;
}

/**
 * Раскладываем игроков по командам так, чтобы самый дорогой состав вышел как можно
 * дешевле: берём игроков от дорогих к дешёвым и каждого отдаём команде, которая
 * сейчас потратила меньше всех. Это классическая жадность для «минимизировать
 * максимум» — она не всегда оптимальна, но даёт РАБОЧИЙ расклад, то есть найденный
 * бюджет заведомо достижим, а не теоретичен.
 */
function packByLeastLoaded(
  captainCosts: number[],
  picks: number[],
  picksPerTeam: number,
): number[] {
  // Объекты вместо параллельных массивов: со строгой индексацией (`number | undefined`)
  // код на индексах превращается в частокол проверок и легко расходится по смыслу.
  const teams = captainCosts.map((total) => ({ total, taken: 0 }));

  for (const cost of [...picks].sort((a, b) => b - a)) {
    const target = teams
      .filter((t) => t.taken < picksPerTeam)
      .reduce<(typeof teams)[number] | null>(
        (best, t) => (best === null || t.total < best.total ? t : best),
        null,
      );
    if (target === null) break;
    target.total += cost;
    target.taken += 1;
  }

  return teams.map((t) => t.total);
}

/** Заявка в объёме, нужном только для расчёта цены и распределения по капитанам. */
export interface DraftRegistrationCost {
  userId?: string | null;
  status: string;
  /** Стоимость игрока в баллах; корректировка модератора приоритетнее авторасчёта. */
  points?: number | null;
  moderatorAdjustedPoints?: number | null;
}

/** Цена одобренной заявки: ручная правка модератора приоритетнее авторасчёта. */
export function registrationCost(r: DraftRegistrationCost): number {
  return r.moderatorAdjustedPoints ?? r.points ?? 0;
}

/**
 * Делит одобренные заявки на цены капитанов и цены остального пула — общий шаг
 * перед вызовом `checkDraftFeasibility`. Вынесено сюда, чтобы панель капитанов
 * (где капитаны уже назначены) и подборщик бюджета в настройках (где их может
 * не быть) не дублировали одну и ту же логику отбора.
 */
export function splitApprovedCosts(
  regs: DraftRegistrationCost[],
  captainUserIds: ReadonlySet<string>,
): { captainCosts: number[]; poolCosts: number[] } {
  const captainCosts: number[] = [];
  const poolCosts: number[] = [];
  for (const r of regs) {
    if (String(r.status || "").toLowerCase() !== "approved" || !r.userId) continue;
    const bucket = captainUserIds.has(r.userId) ? captainCosts : poolCosts;
    bucket.push(registrationCost(r));
  }
  return { captainCosts, poolCosts };
}

export function checkDraftFeasibility(
  input: DraftFeasibilityInput,
): DraftFeasibilityResult {
  const { budget, teamSize, teamsCount } = input;
  const picksPerTeam = Math.max(0, teamSize - 1);
  const playersNeeded = teamsCount * picksPerTeam;

  const pool = [...input.poolCosts].sort((a, b) => a - b);
  const captains = [...input.captainCosts];

  // Капитаны назначены не все — домысливаем недостающих САМЫМИ ДОРОГИМИ из пула.
  // Пессимистично намеренно: капитанами обычно ставят сильных, и если проверка
  // говорит «соберётся» — она не подведёт после реального назначения.
  let assumedCaptains = 0;
  while (captains.length < teamsCount && pool.length > 0) {
    captains.push(pool.pop()!);
    assumedCaptains += 1;
  }

  const base = {
    playersNeeded,
    playersAvailable: pool.length,
    assumedCaptains,
  };

  if (pool.length < playersNeeded) {
    return {
      ...base,
      feasible: false,
      reason: "not_enough_players",
      minBudgetNeeded: 0,
      shortfall: 0,
      exact: true,
      teamTotals: [],
    };
  }

  // Капитан дороже всего бюджета — бэкенд молча обнулит ему остаток, и он не сможет
  // взять никого. Отдельная причина: увеличением бюджета «на чуть-чуть» не лечится.
  const overBudgetCaptain = captains.find((c) => c >= budget);

  // Брать игроков дороже необходимого смысла нет: замена любого выбранного на более
  // дешёвого из пула не ухудшает расклад. Поэтому рассматриваем N самых дешёвых.
  const cheapest = pool.slice(0, playersNeeded);
  const totals = packByLeastLoaded(captains, cheapest, picksPerTeam);
  const minBudgetNeeded = Math.max(...totals);

  // Нижняя граница — чтобы понимать, точен ли ответ:
  //  • самый дорогой капитан всё равно обязан добрать picksPerTeam игроков,
  //    дешевле самых дешёвых в пуле не выйдет;
  //  • и суммарно всё не может быть дешевле среднего по командам.
  const cheapestPicksSum = cheapest
    .slice(0, picksPerTeam)
    .reduce((a, b) => a + b, 0);
  const lowerByCaptain = Math.max(...captains) + cheapestPicksSum;
  const totalCost =
    captains.reduce((a, b) => a + b, 0) + cheapest.reduce((a, b) => a + b, 0);
  const lowerByAverage = Math.ceil(totalCost / teamsCount);
  const lowerBound = Math.max(lowerByCaptain, lowerByAverage);

  const feasible = !overBudgetCaptain && minBudgetNeeded <= budget;

  return {
    ...base,
    feasible,
    reason: feasible
      ? "ok"
      : overBudgetCaptain
        ? "captain_over_budget"
        : "budget_too_low",
    minBudgetNeeded,
    shortfall: Math.max(0, minBudgetNeeded - budget),
    exact: minBudgetNeeded === lowerBound,
    teamTotals: [...totals].sort((a, b) => b - a),
  };
}
